import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { BLOG_POSTS, BUSINESS } from "@/lib/site-data";
import blogOgImage from "@/assets/blog-og.jpg";

const BASE_URL = "https://lehitradessaeo.com";
const PAGE_SIZE = 6;
const TOTAL_PAGES = Math.max(1, Math.ceil(BLOG_POSTS.length / PAGE_SIZE));
const BASE_TITLE = `Trades SEO Blog | ${BUSINESS.shortName}`;
const BASE_DESC =
  "Practical SEO and Google Ads playbooks for contractors — local SEO, Map Pack, LSAs, GBP, and the technical baseline that moves rankings.";
const OG_IMAGE = `${BASE_URL}${blogOgImage}`;

const blogSearchSchema = z.object({
  page: fallback(z.number().int().min(1), 1).default(1),
});

function canonicalFor(page: number) {
  return page <= 1 ? `${BASE_URL}/blog` : `${BASE_URL}/blog?page=${page}`;
}

export const Route = createFileRoute("/blog")({
  validateSearch: zodValidator(blogSearchSchema),
  head: ({ match }) => {
    const page = (match.search as { page?: number } | undefined)?.page ?? 1;
    const inRange = page >= 1 && page <= TOTAL_PAGES;
    const title = page > 1 ? `${BASE_TITLE} — Page ${page}` : BASE_TITLE;
    const canonical = canonicalFor(inRange ? page : 1);
    const start = (page - 1) * PAGE_SIZE;
    const pagePosts = inRange ? BLOG_POSTS.slice(start, start + PAGE_SIZE) : [];

    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: BASE_DESC },
      { property: "og:title", content: title },
      { property: "og:description", content: BASE_DESC },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: BUSINESS.shortName },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1216" },
      { property: "og:image:height", content: "640" },
      { property: "og:image:alt", content: `${BASE_TITLE} — share preview` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: BASE_DESC },
      { name: "twitter:image", content: OG_IMAGE },
    ];
    meta.push({
      name: "robots",
      content: inRange ? "index, follow" : "noindex, follow",
    });

    const links: Array<Record<string, string>> = [
      { rel: "canonical", href: canonical },
    ];
    if (inRange && page > 1) {
      links.push({ rel: "prev", href: canonicalFor(page - 1) });
    }
    if (inRange && page < TOTAL_PAGES) {
      links.push({ rel: "next", href: canonicalFor(page + 1) });
    }

    const scripts = inRange
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "@id": `${canonical}#collectionpage`,
              name: title,
              description: BASE_DESC,
              url: canonical,
              isPartOf: { "@id": `${BASE_URL}/#website` },
              mainEntity: {
                "@type": "ItemList",
                numberOfItems: pagePosts.length,
                itemListOrder: "https://schema.org/ItemListOrderDescending",
                itemListElement: pagePosts.map((p, i) => ({
                  "@type": "ListItem",
                  position: start + i + 1,
                  url: `${BASE_URL}/blog/${p.slug}`,
                  name: p.title,
                })),
              },
            }),
          },
        ]
      : [];

    return { meta, links, scripts };
  },
  component: BlogIndex,
});

function BlogIndex() {
  const { page } = Route.useSearch();
  const inRange = page >= 1 && page <= TOTAL_PAGES;
  const start = (page - 1) * PAGE_SIZE;
  const pagePosts = inRange ? BLOG_POSTS.slice(start, start + PAGE_SIZE) : [];

  return (
    <main className="mx-auto max-w-4xl px-6 py-20">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Blog</p>
      <h1 className="mt-3 font-display text-5xl tracking-tight md:text-6xl">
        Trades SEO, written by people who run it
      </h1>
      <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
        Long-form playbooks for contractors who want to actually understand what
        their SEO agency is doing.
      </p>
      {!inRange ? (
        <div className="mt-12 rounded border border-border p-8 text-center">
          <p className="text-lg font-medium">No posts on this page.</p>
          <Link to="/blog" search={{ page: 1 }} className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
            ← Back to page 1
          </Link>
        </div>
      ) : (
      <>
      <div className="mt-12 divide-y divide-border border-y border-border">
        {pagePosts.map((p) => (
          <article key={p.slug} className="py-8">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {new Date(p.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })} · {p.readMinutes} min read
            </p>
            <h2 className="mt-2 font-display text-3xl tracking-tight">
              <Link to="/blog/$slug" params={{ slug: p.slug }} className="hover:text-primary">
                {p.title}
              </Link>
            </h2>
            <p className="mt-3 text-muted-foreground">{p.excerpt}</p>
            <Link
              to="/blog/$slug"
              params={{ slug: p.slug }}
              className="mt-4 inline-block text-sm font-medium text-primary hover:underline"
            >
              Read the playbook →
            </Link>
          </article>
        ))}
      </div>
      <nav className="mt-10 flex items-center justify-between" aria-label="Pagination">
        {page > 1 ? (
          <Link to="/blog" search={{ page: page - 1 }} rel="prev" className="text-sm font-medium text-primary hover:underline">
            ← Previous
          </Link>
        ) : <span />}
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Page {page} of {TOTAL_PAGES}
        </span>
        {page < TOTAL_PAGES ? (
          <Link to="/blog" search={{ page: page + 1 }} rel="next" className="text-sm font-medium text-primary hover:underline">
            Next →
          </Link>
        ) : <span />}
      </nav>
      </>
      )}
    </main>
  );
}